import React, { RefObject, useState, useEffect, useRef, useMemo, InputHTMLAttributes } from 'react';
import { Button } from '@mui/material';
import {
  AllInputWrap,
  CustomInputText,
  CustomEditorInputWrap,
  AllEditorInputWrap,
} from './CustomInput';
import CrossEditor from '../crosseditor/CrossEditor';
import useEvent from 'react-use-event-hook';
import DOMPurify from 'dompurify';

export enum MsgType {
  ERROR = 'error',
  WARNING = 'warning',
  CONFIRM = 'comfirm',
}

interface PropsType extends InputHTMLAttributes<HTMLInputElement> {
  isWrap?: boolean; // <AllInputWrap> 추가 여부 (default: true)
  editorRef?: RefObject<CrossEditor>;
  name: string;
  isReadOnly?: boolean; // readonly (default: false)
  isError?: boolean; // 오류여부 (추후 삭제 예정 - msgType으로 변경)
  msgError?: string; // 오류문구
  msgWarning?: string; // 경고문구
  msgConfirm?: string; // 확인문구
  msgHelp?: string; // 도움말문구
  msgType?: MsgType; // 노출메세지유형
  onSearchClick?: (e) => void; // 검색버튼 클릭 이벤트
  onKeyActive?: (e) => void; // 에디터 onkeyActive 클릭 이벤트
  onLoaded?: (e) => void; // 에디터 onload 클릭 이벤트
  // className에 find가 포함되는 경우 검색버튼 노출
  // 클릭 이벤트가 정의되지 않은 경우 검색버튼이 disabled 처리됨
  style?: {
    width?: string;
    height?: string;
  }; // <AllInputWrap>에 추가되는 스타일
  // 그 외 속성은 InputHTMLAttributes 그대로 사용
}
const CustomEditorInputWithSearch = (props: PropsType) => {
  const {
    isWrap = true,
    editorRef = undefined,
    name,
    isReadOnly = false,
    isError = false,
    msgError,
    msgHelp,
    msgWarning,
    msgConfirm,
    msgType,
    style,
    onKeyActive,
    onLoaded,
    ...inputProps
  } = props;
  const searchRef = useRef<HTMLDivElement>(null);
  const [isFocus, setFocus] = useState(false);
  const showError = useMemo(() => {
    if (isError) {
      return !isFocus && msgError;
    }
    return false;
  }, [msgError, isError, isFocus]);

  const wrapClass = useMemo(() => {
    if (msgType) {
      if (msgType === MsgType.CONFIRM || !isFocus) {
        return msgType.toString();
      }
    }
    // 도움말이 있는 경우
    if (msgHelp) {
      return 'help';
    }
    return '';
  }, [msgType, msgHelp, isFocus]);

  const msg = useMemo(() => {
    if (msgType && !isFocus) {
      if (msgType === MsgType.ERROR && msgError) {
        return msgError;
      } else if (msgType === MsgType.WARNING && msgWarning) {
        return msgWarning;
      } else if (msgType === MsgType.CONFIRM && msgConfirm) {
        return msgConfirm;
      }
    }
    // 도움말이 있는 경우
    if (msgHelp) {
      return msgHelp;
    }
    return '';
  }, [msgType, msgHelp, isFocus]);

  useEffect(() => {
    setTimeout(() => {
      const handleClick = (e: MouseEvent) => {
        setFocus(false);
        const topIframe = document.querySelector('iframe');
        if (!topIframe) return;

        const topDoc = topIframe.contentDocument || topIframe.contentWindow?.document;
        if (!topDoc) return;

        // 내부 iframe 감지 및 이벤트 등록
        const innerIframe = topDoc.querySelector('iframe');
        if (!innerIframe) return;

        const innerDoc = innerIframe.contentDocument || innerIframe.contentWindow?.document;
        if (!innerDoc) return;
        innerDoc.addEventListener('click', (e) => {
          setFocus(true);
        });
      };

      document.addEventListener('click', handleClick);

      return () => {
        document.removeEventListener('click', handleClick);
      };
    }, 3000);
  }, []);

  return isWrap ? (
    <AllEditorInputWrap
      ref={searchRef}
      className={`${isFocus ? 'focused' : ''} ${showError ? 'error' : wrapClass}`}
      style={style}
    >
      {/* error, warning, comfirm */}
      <CustomEditorInput
        editorRef={editorRef}
        setFocus={setFocus}
        onKeyActive={onKeyActive}
        {...props}
      />
      {showError ? <span>{msgError}</span> : msg && <span>{msg}</span>}
    </AllEditorInputWrap>
  ) : (
    <CustomEditorInput
      editorRef={editorRef}
      setFocus={setFocus}
      onKeyActive={onKeyActive}
      {...props}
    />
  );
};

const CustomEditorInput = (props: any) => {
  const { editorRef, setFocus, onKeyActive, onLoaded, ...inputProps } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <CrossEditor
      {...inputProps}
      ref={editorRef}
      name={inputProps.name}
      params={{
        Width: '100%',
        Height: inputProps?.style?.height || 450,
        Chevron: true,
        Readonly: inputProps?.isReadOnly || false,
        // Readonly: inputProps.isReadOnly,
      }}
      value={inputProps.value || ''}
      onKeyActive={onKeyActive}
      onLoaded={onLoaded}
    />
  );
};
export default CustomEditorInputWithSearch;
